import router, { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import { useState } from "react";

import { ListingStatusPill } from "common/elements/ListingStatusPill";
import { Table } from "common/ui/Table";
import { Stack } from "common/ui/Stack";
import { Paragraph } from "common/ui/Paragraph";
import { Button } from "common/ui/Button";
import { Icon } from "common/ui/Icon";
import { Divider } from "common/ui/Divider";
import { Toast } from "common/ui/Toast";

import { currencyFormatLocale, formatDateMonthYear } from "common/utils/utils";

import { unarchiveListing } from "features/listings/api/unarchiveListing";
import {
  ListingStates,
  mutateLoggedInUserListings,
} from "api/getLoggedInUserListings";

import { ListingLookupDto } from "common/middleware/api.services";

interface ArchivedListingProps {
  listings: ListingLookupDto[]
}

export function ArchivedListingsTable({ listings }: ArchivedListingProps) {
  const { locale } = useRouter();
  const { t } = useTranslation("admin");

  const [isUnarchiving, setIsUnarchiving] = useState(false);
  const [error, setError] = useState("");

  const unarchive = async (propertyId: number) => {
    setError("");
    setIsUnarchiving(true);

    try {
      await unarchiveListing(propertyId);
      await mutateLoggedInUserListings({ filterByListingStates: Object.values(ListingStates).flat() });
    } catch (err) {
      setError(t("somethingWentWrong", { ns: "validation" }));
    }

    setIsUnarchiving(false);
  }

  const rows = listings?.map((listing) => {
    const address = (
      <>
        <Paragraph variant="p1">
          {listing.address.unitNumber}
          {" "}
          {listing.address.street}
        </Paragraph>
        <Paragraph variant="p1">
          {listing.address.city}
          {" "}
          {listing.address.province}
          {" "}
          {listing.address.postalCode}
        </Paragraph>
        <Paragraph variant="p1">
          {listing.address.country}
        </Paragraph>
      </>
    );

    const listPrice = (
      listing.soldDateTimeUtc ? (
        <>
          <Paragraph variant="p1">
            {t("archivedListingsTable.list", {
              price: currencyFormatLocale(listing.price, locale)
            })}
          </Paragraph>
          <Paragraph variant="p1">
            {t("archivedListingsTable.sold", {
              price: currencyFormatLocale(listing.soldPrice, locale)
            })}
          </Paragraph>
        </>
      ) : (
        <Paragraph variant="p1">
          {currencyFormatLocale(listing.price, locale)}
        </Paragraph>
      )
    );

    const status = (
      <ListingStatusPill status={listing.state?.toString()} />
    );

    const date = (
      <Paragraph variant="p1">
        {listing.state?.toString() === "sold" ? (
          t("archivedListingsTable.soldOn", {
            date: formatDateMonthYear(locale, listing.soldDateTimeUtc)
          })
        ) : listing.state?.toString() === "cancelled" ? (
          t("archivedListingsTable.cancelledOn", {
            date: formatDateMonthYear(locale, listing.cancelledDateTimeUtc)
          })
        ) : listing.state?.toString() === "denied" ? (
          t("archivedListingsTable.deniedOn", {
            date: formatDateMonthYear(locale, listing.approvedOrDeniedListingDateTimeUtc)
          })
        ) : (
          t("archivedListingsTable.archivedOn", {
            date: formatDateMonthYear(locale, listing.archivedDateTimeUtc)
          })
        )}
      </Paragraph>
    );

    const quickActions = (
      <Stack direction="row" gap={16}>
        <Button
          variant="inline"
          onClick={(e) => {
            e.stopPropagation();
            router.push(`/listings/${listing.id}`);
          }}
          title={t("shared.preview")}
        >
          <Icon icon={"eye"} />
        </Button>
        <Button
          variant="inline"
          onClick={(e) => {
            e.stopPropagation();
            unarchive(listing.id);
          }}
          title={t("shared.unarchive")}
          isLoading={isUnarchiving}
        >
          <Icon icon={"restart"} />
        </Button>
      </Stack>
    );

    return {
      id: listing.id,
      items: [
        address,
        listPrice,
        status,
        date,
        quickActions
      ]
    }
  });

  return (
    listings?.length === 0 ? (
      <>
        <Divider />
        <br/>
        <Paragraph variant="p1">
          {t("archivedListingsTable.noProperties")}
        </Paragraph>
      </>
    ) : (
      <>
        <Table
          headers={[
            t("common.address"),
            t("common.listPrice"),
            t("common.status"),
            t("common.date"),
            ""
          ]}
          rows={rows}
        />
        {error && (
          <Toast
            close={() => setError("")}
            variant="critical"
            timeout={4000}
            alertIcon
          >
            {error}
          </Toast>
        )}
      </>
    )
  )
}
